/**
 * Client-side plan gating for paid-only features.
 *
 * The server is the enforcer (it refuses the call with a 403 tagged
 * `PLAN_REQUIRED`); this module exists so the UI can (a) show a "needs a paid
 * plan" surface instead of a button that will fail, and (b) turn a gate refusal
 * that slips through anyway — plan lapsed mid-session, billing query errored —
 * into the plans dialog rather than a dead-end toast.
 */
import { ANALYTICS_EVENTS } from '@dunea/shared';
import { useQuery } from '@tanstack/react-query';
import { apiGetBillingOverview, type SubscriptionTier } from '~/lib/api/billing';
import { capture } from '~/lib/posthog';
import { hasPaidPlan } from '~/lib/plans-model';
import { openAccountSettings } from '~/lib/stores/accountSettings';

/** Matches `PLAN_REQUIRED_CODE` on the server (`custom-domain-eligibility.ts`). */
export const PLAN_REQUIRED_CODE = 'PLAN_REQUIRED';

/**
 * Matches `PUBLISH_PAYMENT_REQUIRED_CODE` on the server (`deploy-eligibility.ts`).
 * The user HAS a (Starter) plan but this chat needs the one-time $1 publish
 * payment — distinct from PLAN_REQUIRED (which means "no plan, subscribe").
 *
 * PAUSED 2026-09-04: nothing throws this any more — every paid plan publishes
 * for free. Kept so an older server's refusal is still recognisable, and so
 * restoring the charge doesn't have to re-add the constant.
 */
export const PUBLISH_PAYMENT_REQUIRED_CODE = 'PUBLISH_PAYMENT_REQUIRED';

export interface PlanAccess {
  tier: SubscriptionTier | undefined;
  /** Known to be on the free plan. False while the plan is still loading. */
  isFree: boolean;
  /** Plan not resolved yet — render a placeholder, not the locked state. */
  planUnknown: boolean;
}

/**
 * The user's plan, from the canonical `['billing-overview']` query.
 *
 * `isFree` is deliberately true only for a *loaded* free plan, so a paying user
 * never flashes a locked surface while the query is in flight (and an errored
 * query fails open — the server gate still holds).
 */
export function usePlanAccess(): PlanAccess {
  const { data, isLoading, error } = useQuery({
    queryKey: ['billing-overview'],
    queryFn: apiGetBillingOverview,
  });

  return {
    tier: data?.tier,
    // a Plus/Pro plan from any product unlocks the website too (server: billing/plan-access.ts)
    isFree: !!data && !hasPaidPlan(data),
    planUnknown: isLoading && !error,
  };
}

/**
 * Which paid-only action was stopped. Mirrors the server's `entry` vocabulary
 * (deployments.controller.ts, export.controller.ts) so both surfaces of
 * `publish_blocked` break down on the same axis.
 *
 * `deploy_panel` is the odd one out: it is a *view*, not a click — a free user
 * opened the Publish panel and met the locked banner. It exists so the click
 * entries have a denominator, because "nobody clicked publish" and "nobody ever
 * looked at publishing" are different problems with opposite fixes.
 */
export type PaywallEntry =
  | 'deploy'
  | 'redeploy'
  | 'rename'
  | 'export'
  | 'remove_badge'
  | 'custom_domain'
  | 'deploy_panel'
  | 'view_code';

/**
 * Count a paywall refusal that the UI made without ever calling the server.
 *
 * Every free-tier gate in this app short-circuits into the plans dialog, so the
 * server's own `publish_blocked` can never fire for the population that matters
 * most — free users, who are the entire group being turned away. It fired zero
 * times in 90 days for exactly that reason, leaving the funnel blind at the
 * point of monetisation: a user who wanted their site live looked identical to
 * one who never tried.
 *
 * Call this at the short-circuit, NOT on the upsell dialog's CTA: the block
 * already happened, and counting the follow-on click again would double it.
 */
export function capturePaywallBlock(entry: PaywallEntry, meta?: { tier?: SubscriptionTier; chatId?: string | null }) {
  capture(ANALYTICS_EVENTS.PUBLISH_BLOCKED, {
    reason: 'plan_required',
    surface: 'client',
    entry,
    tier: meta?.tier ?? 'free',
    chatId: meta?.chatId ?? undefined,
  });
}

/**
 * Open the account dialog on the plans tab — the app's single pricing surface.
 * `source` rides through to `plans_viewed` so the pricing funnel can be split by
 * which gate or surface sent the user.
 */
export function showPlans(source = 'unknown'): void {
  openAccountSettings('plans', source);
}

/**
 * Count the block and open the plans dialog — the one-liner for a gate whose
 * only response is the upsell. Gates that show their own paywall surface (the
 * Publish modal) call `capturePaywallBlock` directly instead.
 */
export function blockAndShowPlans(entry: PaywallEntry, meta?: { tier?: SubscriptionTier; chatId?: string | null }) {
  capturePaywallBlock(entry, meta);
  showPlans(entry);
}

/**
 * Handles a failed request that was refused by a paid-plan gate: opens the plans
 * dialog and returns true, so callers can skip their own error toast.
 *
 * Deliberately does NOT capture `publish_blocked`. This path only runs when the
 * request actually reached the server, and the server counted it there — firing
 * again here would double every refusal that slips past the client-side gate.
 *
 * ```ts
 * onError: (err) => {
 *   if (handlePlanRequired(err)) return;
 *   toast.error(...);
 * }
 * ```
 */
export function handlePlanRequired(err: unknown): boolean {
  const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;

  if (code !== PLAN_REQUIRED_CODE) {
    return false;
  }

  showPlans('server_refusal');

  return true;
}
